/**
 * HostLandPromoBanner — banner compatto per hostland.vitadahost.it
 *
 * Focus narrativo:
 *  - Disintermediazione (basta dipendere da Airbnb/Booking)
 *  - Sito brandizzato moderno + guestbook digitale fatto da chi fa l'host
 *  - Prova 1 mese · prezzo annuale "assurdo"
 *
 * Design: compatto (no big hero), gradient pop, logo originale visibile,
 * 2 card prezzo accanto al pitch, dismissibile (persiste 14 giorni).
 *
 * ⚠️ Aggiorna i prezzi qui sotto se cambiano sul sito hostland.
 */
import { useState, useEffect } from 'react';
import { ArrowUpRight, Sparkle, X } from '@phosphor-icons/react';

const STORAGE_KEY = 'hostland_banner_dismissed_until';
const DISMISS_DAYS = 14;

// Aggiorna manualmente se i prezzi cambiano sul sito hostland.vitadahost.it
const PRICING = {
  monthly_trial_label: '1 mese gratis',
  monthly_trial_sub: 'senza carta',
  annual_price: '€59,90',
  annual_label: '/anno',
  annual_sub: 'tutto incluso · prezzo assurdo',
};

export default function HostLandPromoBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const until = localStorage.getItem(STORAGE_KEY);
      if (!until || Date.now() > Number(until)) {
        setVisible(true);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const dismiss = () => {
    try {
      const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(STORAGE_KEY, String(until));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <section
      className="relative overflow-hidden mb-5 group"
      data-testid="hostland-promo-banner"
      aria-label="Prova HostLand"
    >
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-r from-violet-700 via-fuchsia-700 to-rose-600" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-3xl animate-pulse-slow" />
      {/* Grain texture */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay" style={{
        backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 200 200%27><filter id=%27n%27><feTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27/></filter><rect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/></svg>")',
      }} />

      <button
        onClick={dismiss}
        className="absolute top-2 right-2 z-20 w-6 h-6 inline-flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 backdrop-blur transition-all rounded-full"
        aria-label="Chiudi banner"
        data-testid="hostland-banner-dismiss"
      >
        <X size={13} weight="bold" />
      </button>

      <div className="relative z-10 px-4 sm:px-5 py-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-4 items-center">
        {/* Logo originale Hostland */}
        <div className="flex md:flex-col items-center md:items-start gap-3">
          <div className="flex items-center gap-2.5">
            <img
              src="https://hostland.vitadahost.it/hostland-logo.png"
              alt="HostLand logo"
              className="w-12 h-12 sm:w-14 sm:h-14 object-contain bg-white/10 backdrop-blur p-1.5 border border-white/20"
              loading="lazy"
              data-testid="hostland-logo"
            />
            <div className="md:hidden">
              <p className="text-[9px] font-black uppercase tracking-[0.18em] text-white/80">HostLand</p>
              <p className="text-[10px] text-white/70 leading-tight">by VitaDaHost</p>
            </div>
          </div>
          <span className="hidden md:inline-flex items-center gap-1 bg-white/15 backdrop-blur text-white text-[9px] font-black uppercase tracking-[0.16em] px-2 py-0.5 border border-white/20">
            <Sparkle size={9} weight="fill" /> Novità
          </span>
        </div>

        {/* Pitch */}
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg lg:text-xl font-black text-white tracking-tight leading-tight mb-1.5">
            Il tuo <span className="bg-gradient-to-r from-amber-200 to-yellow-400 bg-clip-text text-transparent">sito brandizzato</span> + GuestBook digitale — <span className="underline decoration-amber-300 decoration-2 underline-offset-2">lo costruisci tu</span>.
          </h2>
          <p className="text-white/85 text-[12px] sm:text-[13px] leading-snug">
            <strong>Zero codice.</strong> Trascini, sposti, scrivi e il sito è pronto.
            Modifichi quando vuoi senza chiamare nessuno. Costruito per <strong>ospiti</strong> e <strong>host</strong> da chi fa l'host tutti i giorni.
            <span className="text-white font-bold"> Smetti di dipendere solo da Airbnb e Booking.</span>
          </p>
        </div>

        {/* Pricing cards + CTA */}
        <div className="flex flex-col gap-2 min-w-fit" data-testid="hostland-pricing">
          <div className="flex gap-2">
            <div className="bg-white/12 backdrop-blur-md border border-white/20 px-3 py-2 flex-1 min-w-[110px]" data-testid="price-trial">
              <p className="text-[16px] sm:text-[18px] font-black tabular-nums leading-none bg-gradient-to-br from-cyan-200 to-emerald-300 bg-clip-text text-transparent">
                {PRICING.monthly_trial_label}
              </p>
              <p className="text-[9px] font-bold uppercase tracking-wider text-white/80 mt-1">
                {PRICING.monthly_trial_sub}
              </p>
            </div>
            <div className="bg-white/12 backdrop-blur-md border border-white/20 px-3 py-2 flex-1 min-w-[110px]" data-testid="price-annual">
              <p className="text-[16px] sm:text-[18px] font-black tabular-nums leading-none">
                <span className="bg-gradient-to-br from-amber-200 to-yellow-400 bg-clip-text text-transparent">{PRICING.annual_price}</span>
                <span className="text-white/70 text-[11px] font-bold">{PRICING.annual_label}</span>
              </p>
              <p className="text-[9px] font-bold uppercase tracking-wider text-white/80 mt-1 truncate" title={PRICING.annual_sub}>
                {PRICING.annual_sub}
              </p>
            </div>
          </div>
          <a
            href="https://hostland.vitadahost.it/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-1.5 bg-white hover:bg-amber-50 text-violet-900 text-[11px] font-black uppercase tracking-[0.12em] px-4 py-2.5 transition-all hover:scale-[1.02] active:scale-95 shadow-lg shadow-violet-900/30"
            data-testid="hostland-cta-primary"
          >
            Prova HostLand <ArrowUpRight size={13} weight="bold" />
          </a>
        </div>
      </div>
    </section>
  );
}
